import { Trash2Icon, UploadIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getLogoImageUrl } from './utils/logo';

const MAX_LOGO_FILE_SIZE = 500 * 1024;

type LogoCellProps = {
  isUploading?: boolean;
  logoId: string | null;
  logoPreviewUrl?: string | null;
  tenantName: string;
  onUpload: (file: File) => void;
  onRemove: () => void;
};

export const CustomerIdentityLogoCell = ({
  isUploading = false,
  logoId,
  logoPreviewUrl,
  tenantName,
  onUpload,
  onRemove,
}: LogoCellProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const logoUrl = logoPreviewUrl ?? (logoId ? getLogoImageUrl(logoId) : null);

  const handleUpload = async (file: File | undefined) => {
    if (!file) return;
    if (file.type !== 'image/png' || file.size > MAX_LOGO_FILE_SIZE) {
      setUploadError(t('iconMustBeUnder500Kb'));
      return;
    }

    setUploadError(null);
    onUpload(file);
  };

  return (
    <div className="flex min-w-0 items-center gap-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/png"
        className="sr-only"
        onChange={(event) => handleUpload(event.target.files?.[0])}
      />

      {logoUrl ? (
        <>
          <button
            type="button"
            aria-label={t('changeLogo')}
            className="flex size-8 items-center justify-center overflow-hidden rounded-sm border border-background-darker bg-black"
            disabled={isUploading}
            onClick={() => inputRef.current?.click()}
          >
            <img
              src={logoUrl}
              alt={t('tenantLogoAlt', { tenantName })}
              className="h-full w-full object-contain"
            />
          </button>
          <button
            type="button"
            aria-label={t('removeLogo')}
            className="flex size-7 items-center justify-center rounded-sm text-text-secondary transition-colors hover:bg-background-lighter hover:text-primary-darker"
            disabled={isUploading}
            onClick={onRemove}
          >
            <Trash2Icon aria-hidden="true" className="size-3.5" />
          </button>
        </>
      ) : (
        <div className="flex min-w-0 flex-col items-start gap-2">
          <button
            type="button"
            aria-label={t('uploadPng')}
            disabled={isUploading}
            className="flex h-10 w-[8rem] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-sm border border-background-darker bg-white px-4 text-sm font-semibold text-text-primary transition-colors hover:border-primary-light hover:text-primary-light disabled:pointer-events-none disabled:opacity-50"
            onClick={() => inputRef.current?.click()}
          >
            <UploadIcon aria-hidden="true" className="size-4 shrink-0" />
            <span>{t('uploadPng')}</span>
          </button>
          {uploadError ? (
            <span className="text-sm font-medium text-danger">{uploadError}</span>
          ) : null}
        </div>
      )}
    </div>
  );
};
